import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  FolderOpen, FolderPlus, Search, Link2, Upload, Trash2, FileText, Image as ImageIcon,
  File, Download, ArrowLeft, Loader2, Copy, ExternalLink, RefreshCw, User, Check, ChevronsUpDown, Pencil, BookOpen
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

const CATEGORIES = [
  { value: 'plannen', label: 'Plannen' },
  { value: 'schemas', label: "Schema's" },
  { value: 'offerte', label: 'Offerte' },
  { value: 'keuring', label: 'Keuring' },
  { value: 'facturen', label: 'Facturen' },
  { value: 'fotos', label: "Foto's" },
  { value: 'fotos_reiniging', label: "Foto's Reiniging" },
  { value: 'fotos_installatie', label: "Foto's Installatie" },
  { value: 'datasheets', label: 'Datasheets' },
  { value: 'andere', label: 'Andere' },
];

const catLabel = (v) => CATEGORIES.find(c => c.value === v)?.label || 'Andere';

const fileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return <ImageIcon className="w-5 h-5 text-blue-500" />;
  if (contentType === 'application/pdf') return <FileText className="w-5 h-5 text-red-500" />;
  return <File className="w-5 h-5 text-muted-foreground" />;
};

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileThumb = ({ file, token }) => {
  const [pdfFail, setPdfFail] = useState(false);
  const base = api.defaults.baseURL;
  if (file.content_type?.startsWith('image/')) {
    return <img src={`${base}/public/share/${token}/files/${file.id}`} loading="lazy" className="w-full h-full object-cover" alt={file.filename} />;
  }
  if (file.content_type === 'application/pdf' && !pdfFail) {
    return <img src={`${base}/public/share/${token}/files/${file.id}/thumbnail`} loading="lazy" onError={() => setPdfFail(true)} className="w-full h-full object-cover object-top bg-white" alt={file.filename} />;
  }
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1">
      {fileIcon(file.content_type)}
      <span className="text-[10px] font-semibold text-muted-foreground uppercase">{(file.filename?.split('.').pop() || '').slice(0, 4)}</span>
    </div>
  );
};

export default function Sharepoint() {
  const [folders, setFolders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [newFolder, setNewFolder] = useState({ name: '', customer_id: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [deleteFolderId, setDeleteFolderId] = useState(null);
  const [custSearchOpen, setCustSearchOpen] = useState(false);
  const [custSearchQuery, setCustSearchQuery] = useState('');

  const custLabel = (c) => {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    return c.company_name ? (name ? `${name} — ${c.company_name}` : c.company_name) : name;
  };

  const [activeFolder, setActiveFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('plannen');
  const [uploading, setUploading] = useState(false);
  const [deleteFileId, setDeleteFileId] = useState(null);
  const [renameFile, setRenameFile] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [datasheets, setDatasheets] = useState([]);
  const [dsSelected, setDsSelected] = useState([]);
  const [dsSaving, setDsSaving] = useState(false);
  const [activeProject, setActiveProject] = useState(1);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [addingProject, setAddingProject] = useState(false);

  const fetchFolders = async () => {
    try {
      const res = await api.get('/sharepoint/folders');
      setFolders(res.data);
    } catch { toast.error('Kon mappen niet laden'); }
    finally { setLoading(false); }
  };

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers');
      const list = Array.isArray(res.data) ? res.data : res.data.customers || [];
      setCustomers([...list].sort((a, b) =>
        `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(`${b.first_name || ''} ${b.last_name || ''}`)
      ));
    } catch { /* stil */ }
  };

  useEffect(() => { fetchFolders(); fetchCustomers(); }, []);

  const openFolder = async (folder, projectNum = null) => {
    setActiveFolder(folder);
    const proj = projectNum || folder.project_count || 1;
    setActiveProject(proj);
    try {
      const res = await api.get(`/sharepoint/folders/${folder.id}/files`);
      setFiles(res.data);
      setDsSelected(res.data.filter(f => f.datasheet_id && (f.project || 1) === proj).map(f => f.datasheet_id));
    } catch { toast.error('Kon bestanden niet laden'); }
    try {
      const ds = await api.get('/datasheets');
      setDatasheets(ds.data);
    } catch { /* stil */ }
  };

  const switchProject = (n) => {
    setActiveProject(n);
    setDsSelected(files.filter(f => f.datasheet_id && (f.project || 1) === n).map(f => f.datasheet_id));
  };

  const handleAddProject = async () => {
    setAddingProject(true);
    try {
      const res = await api.post(`/sharepoint/folders/${activeFolder.id}/projects`);
      const updated = { ...activeFolder, project_count: res.data.project_count };
      setActiveFolder(updated);
      switchProject(res.data.project_count);
      toast.success(`Project ${res.data.project_count} aangemaakt`);
      fetchFolders();
    } catch { toast.error('Kon project niet aanmaken'); }
    finally { setAddingProject(false); }
  };

  const applyDatasheets = async (ids) => {
    setDsSaving(true);
    try {
      const res = await api.post(`/sharepoint/folders/${activeFolder.id}/datasheets`, { datasheet_ids: ids, project: activeProject });
      toast.success(`Datasheets bijgewerkt (+${res.data.added} / -${res.data.removed})`);
      openFolder(activeFolder, activeProject);
      fetchFolders();
    } catch { toast.error('Kon datasheets niet bijwerken'); }
    finally { setDsSaving(false); }
  };

  const shareUrl = (folder) => `${window.location.origin}/share/${folder.share_token}`;

  const copyShareLink = (folder) => {
    navigator.clipboard.writeText(shareUrl(folder));
    toast.success('Publieke link gekopieerd!');
  };

  const handleCreate = async () => {
    if (!newFolder.name.trim()) { toast.error('Geef de map een naam'); return; }
    setCreating(true);
    try {
      await api.post('/sharepoint/folders', {
        name: newFolder.name,
        customer_id: newFolder.customer_id || null,
        description: newFolder.description || null
      });
      toast.success('Map aangemaakt');
      setCreateOpen(false);
      setNewFolder({ name: '', customer_id: '', description: '' });
      fetchFolders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kon map niet aanmaken'); }
    finally { setCreating(false); }
  };

  const handleUpload = async (fileList, project, folderOverride = null) => {
    if (!fileList?.length) return;
    const folder = folderOverride || activeFolder;
    setUploading(true);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('category', uploadCategory);
        fd.append('project', String(project));
        await api.post(`/sharepoint/folders/${folder.id}/files`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${fileList.length} bestand(en) geüpload naar Project ${project}`);
      openFolder(folder, project);
      fetchFolders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload mislukt'); }
    finally { setUploading(false); }
  };

  const confirmUpload = async (choice) => {
    const filesToUpload = pendingUpload;
    setPendingUpload(null);
    if (choice === 'new') {
      try {
        const res = await api.post(`/sharepoint/folders/${activeFolder.id}/projects`);
        const updated = { ...activeFolder, project_count: res.data.project_count };
        setActiveFolder(updated);
        await handleUpload(filesToUpload, res.data.project_count, updated);
      } catch { toast.error('Kon project niet aanmaken'); }
      return;
    }
    await handleUpload(filesToUpload, choice);
  };

  const filteredFolders = folders.filter(f =>
    !search ||
    f.name?.toLowerCase().includes(search.toLowerCase()) ||
    f.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  // ============ MAP DETAIL WEERGAVE ============
  if (activeFolder) {
    const projectCount = activeFolder.project_count || 1;
    const projFiles = files.filter(f => (f.project || 1) === activeProject);
    const grouped = CATEGORIES.map(c => ({ ...c, items: projFiles.filter(f => f.category === c.value) })).filter(g => g.items.length > 0);
    return (
      <div className="space-y-6" data-testid="sharepoint-folder-detail">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={() => { setActiveFolder(null); setFiles([]); }} data-testid="sharepoint-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FolderOpen className="w-6 h-6 text-primary" />
                {activeFolder.name}
              </h1>
              {activeFolder.customer_name && (
                <p className="text-sm text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" />{activeFolder.customer_name}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => copyShareLink(activeFolder)} data-testid="copy-share-link-btn">
              <Link2 className="w-4 h-4 mr-2" />
              Publieke link kopiëren
            </Button>
            <Button variant="outline" onClick={() => window.open(shareUrl(activeFolder), '_blank')} data-testid="open-share-link-btn">
              <ExternalLink className="w-4 h-4 mr-2" />
              Bekijk als klant
            </Button>
            <Button variant="outline" size="icon" title="Nieuwe deellink genereren (oude link stopt met werken)"
              onClick={async () => {
                if (!window.confirm('Opgelet: de huidige deellink stopt dan met werken.\n\nAlle eerder gemailde links naar deze klant worden ongeldig. Nieuwe link genereren?')) return;
                try {
                  const res = await api.post(`/sharepoint/folders/${activeFolder.id}/regenerate-token`);
                  const updated = { ...activeFolder, share_token: res.data.share_token };
                  setActiveFolder(updated);
                  navigator.clipboard.writeText(shareUrl(updated));
                  toast.success('Nieuwe link gegenereerd en gekopieerd!');
                  fetchFolders();
                } catch { toast.error('Kon geen nieuwe link genereren'); }
              }} data-testid="regenerate-link-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Projecten */}
        <div className="flex flex-wrap items-center gap-2" data-testid="project-tabs">
          {Array.from({ length: projectCount }, (_, i) => i + 1).map(n => (
            <Button key={n} size="sm" variant={activeProject === n ? 'default' : 'outline'}
              onClick={() => switchProject(n)} data-testid={`project-tab-${n}`}>
              <FolderOpen className="w-3.5 h-3.5 mr-1.5" />
              Project {n}
              <span className="ml-1.5 text-xs opacity-70">({files.filter(f => (f.project || 1) === n).length})</span>
            </Button>
          ))}
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={handleAddProject} disabled={addingProject} data-testid="add-project-btn">
            {addingProject ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5 mr-1.5" />}
            Nieuw project
          </Button>
        </div>

        {/* Upload */}
        <Card>
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div>
              <Label>Categorie</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="w-[180px]" data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <Label>Bestanden (max. 50MB per bestand)</Label>
              <Input type="file" multiple disabled={uploading}
                onChange={(e) => {
                  if (e.target.files?.length) setPendingUpload(Array.from(e.target.files));
                  e.target.value = '';
                }}
                data-testid="sharepoint-upload-input" />
            </div>
            {uploading && <Loader2 className="w-5 h-5 animate-spin text-primary mb-2" />}
          </CardContent>
          {uploadCategory === 'datasheets' && (
            <CardContent className="pt-0 pb-4 px-4">
              <div className="border rounded-lg p-3" data-testid="datasheets-picker">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-primary" />
                  <p className="text-sm font-medium">Beschikbare datasheets — vink aan om toe te voegen aan deze klantmap</p>
                </div>
                {datasheets.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nog geen datasheets in de bibliotheek. Upload ze via Producten &gt; Datasheets.</p>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
                      {datasheets.map(ds => (
                        <label key={ds.id} className="flex items-center gap-2 text-sm p-1.5 rounded hover:bg-muted/50 cursor-pointer" data-testid={`ds-check-${ds.id}`}>
                          <Checkbox
                            checked={dsSelected.includes(ds.id)}
                            onCheckedChange={(checked) => {
                              setDsSelected(prev => checked ? [...prev, ds.id] : prev.filter(x => x !== ds.id));
                            }}
                          />
                          <span className="truncate">{ds.name}</span>
                        </label>
                      ))}
                    </div>
                    <Button size="sm" className="mt-3" disabled={dsSaving}
                      onClick={() => applyDatasheets(dsSelected)} data-testid="apply-datasheets-btn">
                      {dsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                      Datasheets toepassen
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Bestanden per categorie */}
        {projFiles.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-muted-foreground">
            <Upload className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nog geen bestanden in Project {activeProject} — upload plannen, offertes, keuringen...
          </CardContent></Card>
        ) : (
          grouped.map(group => (
            <div key={group.value}>
              <h3 className="font-semibold mb-2 text-sm uppercase tracking-wide text-muted-foreground">{group.label} ({group.items.length})</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {group.items.map(f => (
                  <Card key={f.id} className="overflow-hidden group hover:border-primary/50 transition-colors">
                    <button type="button" className="block w-full aspect-[4/3] bg-muted/40 overflow-hidden border-b border-border/50 relative"
                      onClick={() => window.open(`${api.defaults.baseURL}/public/share/${activeFolder.share_token}/files/${f.id}`, '_blank')}
                      title="Bekijken" data-testid={`file-thumb-${f.id}`}>
                      <FileThumb file={f} token={activeFolder.share_token} />
                      {f.signed && (
                        <span className="absolute top-1.5 left-1.5 text-[10px] font-semibold bg-emerald-600 text-white rounded-full px-2 py-0.5" data-testid={`file-signed-badge-${f.id}`}>Ondertekend</span>
                      )}
                      {f.signable && !f.signed && (
                        <span className="absolute top-1.5 left-1.5 text-[10px] font-semibold bg-amber-500 text-white rounded-full px-2 py-0.5" data-testid={`file-awaiting-signature-${f.id}`}>Wacht op handtekening</span>
                      )}
                    </button>
                    <CardContent className="p-2.5">
                      <p className="font-medium text-sm truncate" title={f.filename}>{f.filename}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{fmtSize(f.size)} · {formatDate(f.uploaded_at)}</p>
                      <div className="flex gap-0.5 mt-1.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Naam wijzigen"
                          onClick={() => { setRenameFile(f); setRenameValue(f.filename.replace(/\.[^.]+$/, '')); }}
                          data-testid={`rename-file-${f.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Downloaden"
                          onClick={() => window.open(`${api.defaults.baseURL}/public/share/${activeFolder.share_token}/files/${f.id}?download=true`, '_blank')}
                          data-testid={`download-file-${f.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" title="Verwijderen"
                          onClick={() => setDeleteFileId(f.id)} data-testid={`delete-file-${f.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}

        {/* Upload: projectkeuze popup */}
        <Dialog open={!!pendingUpload} onOpenChange={(o) => !o && setPendingUpload(null)}>
          <DialogContent className="max-w-md" data-testid="upload-project-dialog">
            <DialogHeader>
              <DialogTitle>In welk project uploaden?</DialogTitle>
              <DialogDescription>
                {pendingUpload?.length} bestand(en) · categorie {catLabel(uploadCategory)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {Array.from({ length: projectCount }, (_, i) => i + 1).map(n => (
                <Button key={n} variant={n === activeProject ? 'default' : 'outline'} className="w-full justify-between"
                  onClick={() => confirmUpload(n)} data-testid={`upload-to-project-${n}`}>
                  <span><FolderOpen className="w-4 h-4 mr-2 inline" />Project {n}</span>
                  <span className="text-xs opacity-70">{files.filter(f => (f.project || 1) === n).length} bestanden</span>
                </Button>
              ))}
              <Button variant="outline" className="w-full border-dashed" onClick={() => confirmUpload('new')} data-testid="upload-to-new-project">
                <FolderPlus className="w-4 h-4 mr-2" />
                Nieuw project aanmaken
              </Button>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPendingUpload(null)}>Annuleren</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bestand hernoemen */}
        <Dialog open={!!renameFile} onOpenChange={(o) => !o && setRenameFile(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Bestandsnaam wijzigen</DialogTitle>
              <DialogDescription>De extensie blijft automatisch behouden.</DialogDescription>
            </DialogHeader>
            <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && document.getElementById('rename-save-btn')?.click()}
              placeholder="Nieuwe bestandsnaam" data-testid="rename-file-input" autoFocus />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameFile(null)}>Annuleren</Button>
              <Button id="rename-save-btn" data-testid="rename-save-btn" onClick={async () => {
                if (!renameValue.trim()) { toast.error('Geef een naam op'); return; }
                try {
                  await api.put(`/sharepoint/files/${renameFile.id}`, { filename: renameValue.trim() });
                  toast.success('Bestandsnaam gewijzigd');
                  setRenameFile(null);
                  openFolder(activeFolder, activeProject);
                } catch { toast.error('Kon naam niet wijzigen'); }
              }}>Opslaan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteFileId} onOpenChange={(o) => !o && setDeleteFileId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Bestand verwijderen?</AlertDialogTitle>
              <AlertDialogDescription>Dit bestand wordt definitief verwijderd, ook uit de publieke deelpagina.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuleren</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={async () => {
                try {
                  await api.delete(`/sharepoint/files/${deleteFileId}`);
                  toast.success('Bestand verwijderd');
                  setDeleteFileId(null);
                  openFolder(activeFolder, activeProject);
                  fetchFolders();
                } catch { toast.error('Kon bestand niet verwijderen'); }
              }}>Verwijderen</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ============ MAPPEN OVERZICHT ============
  return (
    <div className="space-y-6" data-testid="sharepoint-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Sharepoint</h1>
          <p className="text-muted-foreground text-sm">Klantmappen met publieke deellinks</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="new-folder-btn">
          <FolderPlus className="w-4 h-4 mr-2" />
          Nieuwe map
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Zoek op mapnaam of klant..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="folder-search-input" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : filteredFolders.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
          Nog geen mappen. Maak een klantmap aan om documenten te delen.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFolders.map(folder => (
            <Card key={folder.id} className="hover:border-primary/50 transition-colors cursor-pointer group" data-testid={`folder-card-${folder.id}`}>
              <CardContent className="p-4" onClick={() => openFolder(folder)}>
                <div className="flex items-start justify-between">
                  <FolderOpen className="w-8 h-8 text-primary mb-2" />
                  <Badge variant="secondary">{folder.file_count} bestanden</Badge>
                </div>
                <h3 className="font-semibold truncate">{folder.name}</h3>
                {folder.customer_name && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 truncate"><User className="w-3 h-3" />{folder.customer_name}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Aangemaakt {formatDate(folder.created_at)}</p>
                <div className="flex gap-1 mt-3" onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => copyShareLink(folder)} data-testid={`copy-link-${folder.id}`}>
                    <Copy className="w-3 h-3 mr-1" />Link
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(shareUrl(folder), '_blank')} title="Bekijk als klant">
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500" onClick={() => setDeleteFolderId(folder.id)} data-testid={`delete-folder-${folder.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Nieuwe map dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuwe klantmap</DialogTitle>
            <DialogDescription>Maak een map aan om documenten met een klant te delen via een publieke link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mapnaam *</Label>
              <Input placeholder="bv. Installatie Janssens - Gent" value={newFolder.name}
                onChange={(e) => setNewFolder(prev => ({ ...prev, name: e.target.value }))} data-testid="folder-name-input" />
            </div>
            <div>
              <Label>Klant (optioneel)</Label>
              <Popover open={custSearchOpen} onOpenChange={setCustSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="folder-customer-select">
                    {newFolder.customer_id
                      ? (() => { const c = customers.find(x => x.id === newFolder.customer_id); return c ? custLabel(c) : 'Selecteer klant...'; })()
                      : 'Typ om klant te zoeken...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[420px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Typ klantnaam of bedrijf..." value={custSearchQuery} onValueChange={setCustSearchQuery} data-testid="folder-customer-search-input" />
                    <CommandList>
                      <CommandEmpty>Geen klanten gevonden</CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="none" onSelect={() => {
                          setNewFolder(prev => ({ ...prev, customer_id: '' }));
                          setCustSearchOpen(false); setCustSearchQuery('');
                        }}>
                          <Check className={`mr-2 h-4 w-4 ${!newFolder.customer_id ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="text-muted-foreground">-- Geen klant --</span>
                        </CommandItem>
                        {customers
                          .filter(c => !custSearchQuery || custLabel(c).toLowerCase().includes(custSearchQuery.toLowerCase()) || c.email?.toLowerCase().includes(custSearchQuery.toLowerCase()))
                          .slice(0, 25)
                          .map(c => (
                            <CommandItem key={c.id} value={c.id} onSelect={() => {
                              setNewFolder(prev => ({
                                ...prev,
                                customer_id: c.id,
                                name: prev.name || custLabel(c)
                              }));
                              setCustSearchOpen(false); setCustSearchQuery('');
                            }}>
                              <Check className={`mr-2 h-4 w-4 ${newFolder.customer_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                              <div className="flex flex-col">
                                <span className="font-medium">{custLabel(c)}</span>
                                {c.email && <span className="text-xs text-muted-foreground">{c.email}</span>}
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Omschrijving (optioneel)</Label>
              <Textarea rows={2} placeholder="bv. Documenten zonnepanelen installatie" value={newFolder.description}
                onChange={(e) => setNewFolder(prev => ({ ...prev, description: e.target.value }))} data-testid="folder-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuleren</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="create-folder-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-2" />}
              Map aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Map verwijderen */}
      <AlertDialog open={!!deleteFolderId} onOpenChange={(o) => !o && setDeleteFolderId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Map verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>De map en álle bestanden erin worden definitief verwijderd. De publieke link stopt met werken.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={async () => {
              try {
                await api.delete(`/sharepoint/folders/${deleteFolderId}`);
                toast.success('Map verwijderd');
                setDeleteFolderId(null);
                fetchFolders();
              } catch { toast.error('Kon map niet verwijderen'); }
            }}>Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
